import { Box, Grid2, Skeleton, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { ISchoolMonitoringEventSimpleProps } from '../../../services/Events/SchoolMonitoringEventService/types';
import { SchoolMonitoringType } from '../../../services/Events/SchoolMonitoringEventService/utils'; // Importe o Enum
import { useGetStudentRecognitionByIdAsync } from '../../../services/Recognition/Student/hooks/useMutation';
import { useGetResponsibleByIdAsync } from '../../../services/ResponsibleService/hooks/useMutation';

interface LastSchoolEventProps {
  event: ISchoolMonitoringEventSimpleProps;
  handleViewImage: (imageUrl: string) => void;
}

const LastSchoolEvent = ({ event, handleViewImage }: LastSchoolEventProps) => {
  const [boundResponsibleOrStudents, setBoundResponsibleOrStudents] = useState<any[]>([]);
  const [loadingBound, setLoadingBound] = useState(false);

  const { mutateAsync: mutateGetStudentRecognitionAsync } = useGetStudentRecognitionByIdAsync();
  const { mutateAsync: mutateGetResponsibleAsync } = useGetResponsibleByIdAsync();

  const isStudentEvent = 
    event?.schoolMonitoringType === SchoolMonitoringType.STUDENT || 
    event?.schoolMonitoringType === 0;

  const getBoundResponsibleOrStudents = useCallback(async () => {
    if (!event) return [];
    
    setLoadingBound(true);
    try {
      if (isStudentEvent) {
          const ids = event.studentRecognition?.responsibleIds || [];
          if (ids.length === 0) return [];

          const promises = ids.map((id) => mutateGetResponsibleAsync(id.toString()));
          const results = await Promise.all(promises);
          return results.filter((v) => !!v);
      } 
      else {
          const ids = event.responsible?.studentIds || [];
          if (ids.length === 0) return [];

          const promises = ids.map((id) => mutateGetStudentRecognitionAsync(id));
          const results = await Promise.all(promises);
          return results.filter((v) => !!v);
      }
      
    } catch (error) {
      console.error('Erro ao buscar vinculados:', error);
      return [];
    } finally {
        setLoadingBound(false);
    }
  }, [event, isStudentEvent, mutateGetResponsibleAsync, mutateGetStudentRecognitionAsync]);

  useEffect(() => {
    getBoundResponsibleOrStudents().then(setBoundResponsibleOrStudents);
  }, [event, getBoundResponsibleOrStudents]);

  const getMainName = () => {
      if (isStudentEvent) 
          return event.studentRecognition?.name;
      return event.responsible?.name;
  };

  return (
    <Grid2 container columnSpacing={2} rowSpacing={2} sx={{ flexGrow: 1, alignItems: 'stretch' }}>
      {/* --- CARTÃO PRINCIPAL (EVENTO) --- */}
      <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
        <Card
          sx={{
            width: '100%',
            p: 2,
            backgroundColor: 'rgba(41,50,84,0.53)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Chip de Tipo */}
          <Box sx={{ position: 'absolute', right: 15, top: 10, zIndex: 1 }}>
            {isStudentEvent ? (
              <Chip label={t('common.student')} color="success" size="small" />
            ) : (
              <Chip label={t('common.responsible')} color="primary" size="small" />
            )}
          </Box>
          
          {/* Data */}
          <Typography 
              sx={{ 
                position: 'absolute', 
                bottom: 10, right: 10, 
                fontSize: '0.875rem', fontWeight: 'bold' 
              }}
           >
              {new Date(event.createdAt).toLocaleString()}
           </Typography>

          <Grid2 container spacing={2}>
             <Grid2 size={{ xs: 12, sm: 4 }}>
                {event.recognitionImage ? (
                    <img
                        src={event.recognitionImage}
                        alt="recognition"
                        onClick={() => handleViewImage(event.recognitionImage || '/placeholder.png')}
                        style={{ width: '100%', maxHeight: 200, borderRadius: 4, cursor: 'pointer',  objectFit: 'cover' }}
                    />
                ) : (
                    <Skeleton variant="rectangular" height={150} />
                )}
             </Grid2>

             {/* Detalhes Principais */}
             <Grid2 size={{ xs: 12, sm: 8 }}>
                <Typography variant="caption" color="primary.main" fontWeight="bold">
                    {t('common.name')}
                </Typography>
                <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {getMainName()}
                </Typography>

                <Typography variant="caption" color="primary.main" fontWeight="bold">
                    {t('common.device')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                    {event.device.name}
                </Typography>
             </Grid2>
          </Grid2>
        </Card>
      </Grid2>

      {/* --- CARTÃO SECUNDÁRIO (VINCULADOS) --- */}
      <Grid2 size={{ xs: 12, md: 6 }} container spacing={2}>
         {loadingBound ? (
             <Grid2 size={12}><Skeleton variant="rectangular" height="100%" /></Grid2>
         ) : boundResponsibleOrStudents.length === 0 ? (
             <Grid2 size={12}>
                 <Card sx={{ p: 4, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(41,50,84,0.53)' }}>
                     <Typography color="text.secondary">
                        {isStudentEvent
                            ? t('schoolMonitoring.noResponsiblesAttached') 
                            : t('schoolMonitoring.noStudentsAttached')}
                     </Typography>
                 </Card>
             </Grid2>
         ) : (
             boundResponsibleOrStudents.map((bound, idx) => (
                 <Grid2 size={{ xs: 12, sm: 6 }} key={idx}>
                     <Card sx={{ p: 2, height: '100%', backgroundColor: 'rgba(41,50,84,0.53)', display: 'flex', gap: 2, alignItems: 'center', position: 'relative' }}>
                        <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                          {isStudentEvent ? (
                            <Chip 
                              label={t('common.responsible')} 
                              color="primary" 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.65rem' }} 
                            />
                          ) : (
                            <Chip 
                              label={t('common.student')} 
                              color="success" 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                                          
                         <img 
                            src={bound.image || '/placeholder.png'} 
                            alt={bound.name} 
                            style={{ width: 60, height: 60, borderRadius: 4, cursor: 'pointer', objectFit: 'cover' }} 
                            onClick={() => handleViewImage(bound.image)}
                         />
                         <Box>
                             <Typography variant="caption" color="primary.main" fontWeight="bold">
                                {t('common.name')}
                             </Typography>
                             <Typography variant="body2" fontWeight="bold"sx={{ 
                                  paddingRight: '10px', 
                                  wordBreak: 'break-word'
                                }}
                              >
                                {bound.name}
                             </Typography>
                         </Box>
                     </Card>
                 </Grid2>
             ))
         )}
      </Grid2>
    </Grid2>
  );
};

export default LastSchoolEvent;